import { Application, Assets } from 'pixi.js';
import levelsJson from '../game/levels/levels.json';
import rifleUrl from '../arts/weapons/rifle.png';
import arena1BgUrl from '../arts/backgrounds/arena1_bg.jpg';
import mainBgUrl from '../arts/backgrounds/main_bg.jpg';
import clickUrl from '../arts/sounds/click.mp3';
import walkUrl from '../arts/sounds/walk.mp3';
import vicUrl from '../arts/sounds/vic.mp3';
import reloadUrl from '../arts/sounds/reload.mp3';
import pewUrl from '../arts/sounds/pew.mp3';
import jumpUrl from '../arts/sounds/jump.mp3';
import hitUrl from '../arts/sounds/hit.mp3';
import deaUrl from '../arts/sounds/dea.mp3';
import loseUrl from '../arts/sounds/lose.mp3';
import musicIntroUrl from '../arts/sounds/music_intro.mp3';
import musicB2Url from '../arts/sounds/b2.mp3';
import musicB3Url from '../arts/sounds/b3.mp3';
import musicLoopUrl from '../arts/sounds/music_loop.mp3';
import menuMusicUrl from '../arts/sounds/menu_music.mp3';
import flagRedUrl from '../arts/player/flag_red.png';
import flagBlueUrl from '../arts/player/flag_blue.png';
import platformHUrl from '../arts/platforms/h.png';
import platformVUrl from '../arts/platforms/v.png';
import platformMUrl from '../arts/platforms/m.png';
import spikeUrl from '../arts/platforms/s.png';
import { Game } from './game/Game';
import { LoadingScreen, preloadAssetsWithProgress, type LoadableAsset } from './game/LoadingScreen';
import { MainMenu } from './game/MainMenu';
import { showOnlineDialog } from './game/OnlineDialog';
import { cucumberUrl, skinUrls, skinUrlsById } from './game/skins';
import type { LevelsData } from './types';

async function boot(): Promise<void> {
  const data = levelsJson as unknown as LevelsData;
  const mount = document.getElementById('app');
  if (!mount) throw new Error('#app mount not found');
  mount.style.position = 'relative';

  const loadingScreen = new LoadingScreen(mount);

  const app = new Application();
  await app.init({
    width: data.config.screenWidth,
    height: data.config.screenHeight,
    backgroundColor: 0x000000,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
  });

  const imageAssets: LoadableAsset[] = [
    { url: mainBgUrl, label: 'main-bg' },
    { url: arena1BgUrl, label: 'arena' },
    { url: rifleUrl, label: 'rifle' },
    { url: platformHUrl, label: 'platform-h' },
    { url: platformVUrl, label: 'platform-v' },
    { url: platformMUrl, label: 'platform-m' },
    { url: spikeUrl, label: 'spike' },
    { url: flagRedUrl, label: 'flag-red' },
    { url: flagBlueUrl, label: 'flag-blue' },
    ...skinUrls.map(url => ({ url, label: 'skin' })),
  ];
  loadingScreen.setLabel('Загружаем графику...');
  await preloadAssetsWithProgress(imageAssets, progress => loadingScreen.setProgress(progress));

  loadingScreen.setLabel('Готовим текстуры...');
  await Assets.load([...skinUrls, rifleUrl, arena1BgUrl, platformHUrl, platformVUrl, platformMUrl, spikeUrl, flagRedUrl, flagBlueUrl]);
  mount.appendChild(app.canvas);

  const assets = { musicB2Url, musicB3Url, cucumberUrl, skinUrls, skinUrlsById, rifleUrl, flagRedUrl, flagBlueUrl, arena1BgUrl, platformHUrl, platformVUrl, platformMUrl, spikeUrl, walkUrl, vicUrl, reloadUrl, pewUrl, jumpUrl, hitUrl, deaUrl, loseUrl, musicIntroUrl, musicLoopUrl, menuMusicUrl };
  const game = new Game(app, data, assets);

  // База громкости фоновой музыки меню (умножается на пользовательский master).
  const MENU_MUSIC_BASE = 0.4;
  let savedMusicVolume = 1;
  let savedSoundVolume = 1;

  // применяем сохранённые настройки
  try {
    const saved = localStorage.getItem('gameSettings');
    if (saved) {
      const s = JSON.parse(saved);
      if (s.music === false) game.setMusicEnabled(false);
      if (s.sound === false) game.setSoundEnabled(false);
      if (s.lang) game.setLang(s.lang);
      if (typeof s.musicVolume === 'number') {
        savedMusicVolume = Math.max(0, Math.min(1, s.musicVolume));
        game.setMusicVolume(savedMusicVolume);
      }
      if (typeof s.soundVolume === 'number') {
        savedSoundVolume = Math.max(0, Math.min(1, s.soundVolume));
        game.setSoundVolume(savedSoundVolume);
      }
    }
  } catch {}

  const menuMusic = new Audio(menuMusicUrl);
  menuMusic.preload = 'none';
  menuMusic.loop = true;
  menuMusic.volume = MENU_MUSIC_BASE * savedMusicVolume;

  const leaveMenu = () => {
    document.removeEventListener('click', startMenuMusic);
    document.removeEventListener('keydown', startMenuMusic);
    menu.hide();
    menuMusic.pause();
    menuMusic.currentTime = 0;
  };

  const menu = new MainMenu(mount, { mainBgUrl, clickUrl }, (action) => {
    if (action === 'test1') {
      leaveMenu();
      game.start();
      game.gotoLevel(1);
    } else if (action === 'testCtf') {
      leaveMenu();
      const data2 = levelsJson as unknown as LevelsData;
      game.start();
      game.gotoLevel(data2.levels.length);
    } else if (action === 'online') {
      showOnlineDialog({ mount, game, leaveMenu });
    }
  }, (change) => {
    // музыка
    if (change.music !== undefined) {
      if (change.music) {
        menuMusic.play().catch(() => {});
      } else {
        menuMusic.pause();
      }
      game.setMusicEnabled(change.music);
    }
    // звук
    if (change.sound !== undefined) {
      game.setSoundEnabled(change.sound);
    }
    // громкость музыки (мастер)
    if (change.musicVolume !== undefined) {
      menuMusic.volume = MENU_MUSIC_BASE * change.musicVolume;
      game.setMusicVolume(change.musicVolume);
    }
    // громкость звуков (мастер)
    if (change.soundVolume !== undefined) {
      game.setSoundVolume(change.soundVolume);
    }
    if (change.lang !== undefined) {
      game.setLang(change.lang);
    }
  });

  // браузер не разрешает autoplay — запускаем после первого клика
  // но только если музыка включена в настройках
  let menuMusicStarted = false;
  const isMusicEnabled = (): boolean => {
    try {
      const s = localStorage.getItem('gameSettings');
      if (s) return JSON.parse(s).music !== false;
    } catch {}
    return true;
  };
  const startMenuMusic = () => {
    if (!menuMusicStarted && isMusicEnabled()) {
      menuMusicStarted = true;
      menuMusic.play().catch(() => {});
    }
    document.removeEventListener('click', startMenuMusic);
    document.removeEventListener('keydown', startMenuMusic);
  };
  document.addEventListener('click', startMenuMusic);
  document.addEventListener('keydown', startMenuMusic);

  // колбэк выхода из боя → возврат в меню
  game.setExitCallback(() => {
    menuMusicStarted = false;
    if (isMusicEnabled()) menuMusic.play().catch(() => {});
    menu.show();
  });

  menu.show();
  loadingScreen.hide();

  const params = new URLSearchParams(window.location.search);
  const levelParam = params.get('level');
  if (levelParam) {
    const n = parseInt(levelParam, 10);
    if (Number.isFinite(n)) {
      menu.hide();
      game.gotoLevel(n);
      game.start();
    }
  }

  (window as unknown as { __game: Game }).__game = game;
}

void boot();
